import { Grid, Skeleton } from '@mui/material';
import { CardSection, CardSubSection, Match, Switch } from '@side/pantry';
import { useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import type { AgentContact } from '@/common/types/interaction';
import { formatAddress } from '@/common/utils/address';
import { getFullName } from '@/common/utils/person';
import { formatPhoneNumber } from '@/common/utils/phoneNumber';
import { ReadOnlyField } from '@/components/ReadOnlyField';
import { TrackOfferEvent } from '$analytics/useTrackAnalytics/domains/offers/events';
import { useTrackOfferAnalytics } from '$analytics/useTrackAnalytics/domains/offers/useTrackOfferAnalytics';
import { useListingSellerAgent } from '../hooks/useListingSellerAgent';
import type { CreateOfferFormData } from '../types';
import { SellerAgentAutocomplete } from './SellerAgentAutocomplete';

const FIELD_SIZE = { xs: 6, lg: 4 };

const ListingAgentFields = ({ agent }: { agent: AgentContact }) => {
  const brokerage = agent.brokerage ?? null;

  return (
    <Grid container spacing={4.5}>
      <Grid size={FIELD_SIZE}>
        <ReadOnlyField
          label="Name"
          value={getFullName(agent.firstName, agent.lastName) || null}
        />
      </Grid>
      <Grid size={FIELD_SIZE}>
        <ReadOnlyField label="Email" value={agent.email || null} />
      </Grid>
      <Grid size={FIELD_SIZE}>
        <ReadOnlyField
          label="Cell Phone #"
          value={agent.cellPhone ? formatPhoneNumber(agent.cellPhone) : null}
        />
      </Grid>
      <Grid size={FIELD_SIZE}>
        <ReadOnlyField
          label="Brokerage License #"
          value={brokerage?.licenseNumber || null}
        />
      </Grid>
      <Grid size={FIELD_SIZE}>
        <ReadOnlyField
          label="Brokerage Name"
          value={brokerage?.companyName || null}
        />
      </Grid>
      <Grid size={FIELD_SIZE}>
        <ReadOnlyField
          label="Agent License #"
          value={agent.licenseNumber || null}
        />
      </Grid>
      <Grid size={12}>
        <ReadOnlyField
          label="Address"
          value={
            formatAddress(
              {
                street1: brokerage?.street1,
                street2: brokerage?.street2,
                city: brokerage?.city,
                state: brokerage?.state,
                zip: brokerage?.zip,
              },
              { includeStreet2: true },
            ) || null
          }
        />
      </Grid>
    </Grid>
  );
};

const SKELETON_FIELDS = [
  'Name',
  'Email',
  'Cell Phone #',
  'Brokerage License #',
  'Brokerage Name',
  'Agent License #',
];

/** Keeps the labels in place while the listing's agent resolves. */
const ListingAgentFieldsSkeleton = () => (
  <Grid container spacing={4.5}>
    {SKELETON_FIELDS.map((label) => (
      <Grid key={label} size={FIELD_SIZE}>
        <ReadOnlyField label={label} value={<Skeleton width={120} />} />
      </Grid>
    ))}
    <Grid size={12}>
      <ReadOnlyField label="Address" value={<Skeleton width={240} />} />
    </Grid>
  </Grid>
);

/**
 * Seller's Agent card. When the offer is linked to a listing the agent comes
 * from that listing and is read-only; otherwise it's picked manually.
 */
export const SellerAgentSection = () => {
  const { agent, loading } = useListingSellerAgent();
  const listingId = useWatch<CreateOfferFormData, 'listingId'>({
    name: 'listingId',
  });
  const trackOfferEvent = useTrackOfferAnalytics();

  useEffect(() => {
    if (agent) {
      trackOfferEvent(TrackOfferEvent.SellerAgentAutofillCardMount, {
        listingId,
      });
    }
  }, [agent, listingId, trackOfferEvent]);

  return (
    <CardSection cardTitle="Seller's Agent">
      <CardSubSection>
        <Switch fallback={<SellerAgentAutocomplete />}>
          <Match when={loading}>
            <ListingAgentFieldsSkeleton />
          </Match>
          <Match when={agent}>
            {agent && <ListingAgentFields agent={agent} />}
          </Match>
        </Switch>
      </CardSubSection>
    </CardSection>
  );
};
